import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { RefreshCw, Mail, Clock, CreditCard, ShieldCheck, TrendingUp } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'
import { legalCss } from './PrivacyPolicy'

const B = 'https://pixner.net/bankio/main/assets/images'

const sections = [
  { n: '01', title: 'Overview', body: <p className="legal-p">This policy outlines the process and conditions under which refunds are processed through the A1XPAY payment platform. As a payment service provider, A1XPAY facilitates refunds on behalf of merchants — the ultimate refund policy for end-customers is set by the merchant from whom they made the purchase.</p> },
  { n: '02', title: 'Merchant Platform Fees', body: <><p className="legal-p">Monthly platform subscription fees (Business and Enterprise plans) are non-refundable once the billing period has commenced. If you cancel your subscription, you retain access until the end of the paid period and will not be charged again.</p><p className="legal-p">One-time setup fees, if applicable, are non-refundable after account activation.</p></> },
  { n: '03', title: 'Transaction Refunds (Merchant-Initiated)', body: <><p className="legal-p">Merchants may initiate refunds for their customers through the A1XPAY dashboard or via our Refund API. The following conditions apply:</p><ul className="legal-ul"><li>Refunds can be initiated within 180 days of the original transaction date.</li><li>Partial refunds are supported for all payment methods.</li><li>The MDR (transaction fee) charged on the original payment is not refunded to the merchant.</li><li>Refunds to UPI IDs and bank accounts are processed within 2–5 business days.</li><li>Refunds to credit/debit cards may take 5–7 business days depending on the issuing bank.</li></ul></> },
  { n: '04', title: 'Failed Transactions', body: <p className="legal-p">If a payment is deducted from your customer's account but the transaction is marked as failed or pending by the payment network, the amount is automatically refunded within 5–7 business days. A1XPAY is not responsible for delays caused by the customer's bank.</p> },
  { n: '05', title: 'Chargebacks', body: <><p className="legal-p">A chargeback is a transaction reversal initiated by the customer's bank. When a chargeback is filed:</p><ul className="legal-ul"><li>A1XPAY will notify the merchant within 2 business days.</li><li>Merchants have 7 days to submit evidence contesting the chargeback.</li><li>The disputed amount is held until the chargeback is resolved (typically 30–45 days).</li><li>A chargeback processing fee of ₹250 per case applies regardless of outcome.</li></ul></> },
  { n: '06', title: 'How to Request a Refund', body: <><p className="legal-p">Merchants: Log into your dashboard → Transactions → Select transaction → Click "Initiate Refund", or use the Refund API.</p><p className="legal-p">End customers: Contact the merchant you purchased from directly. If you cannot reach the merchant, email support@a1xpay.com with your transaction ID and we will assist.</p></> },
  { n: '07', title: 'Contact', body: <p className="legal-p">For refund-related queries, email support@a1xpay.com or call +91 99103 73510 (Mon–Sat, 10 AM–7 PM IST).</p> },
]

export default function ReturnRefundPolicy() {
  return (
    <>
      <style>{legalCss}</style>
      <div className="legal-root">
        <Navbar />
        <section className="legal-hero">
          <div className="legal-container">
            <div className="legal-hero-grid">
              <motion.div className="legal-hero-text" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
                <div className="legal-hero-icon"><RefreshCw size={26} /></div>
                <span className="legal-sub">Legal Document</span>
                <h1 className="legal-hero-h1">Return &amp; Refund Policy</h1>
                <p className="legal-hero-p">Clear guidelines on how refunds, chargebacks, and failed transactions are handled on the A1XPAY platform.</p>
                <span className="legal-hero-date">Last updated: October 1, 2024</span>
              </motion.div>
              <motion.div className="legal-hero-visual" initial={{ opacity: 0, x: 40, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                <div className="legal-hero-illus">
                  <img src={`${B}/counter-Illus.png`} alt="Refund stats illustration" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        <section className="legal-body">
          <div className="legal-body-container">
            {sections.map((s, i) => (
              <motion.div key={s.n} className="legal-section-card"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.04 }}>
                <div className="legal-h2-wrap">
                  <div className="legal-h2-num">{s.n}</div>
                  <h2 className="legal-h2">{s.title}</h2>
                </div>
                {s.body}
              </motion.div>
            ))}
            <div className="legal-cta">
              <div className="legal-cta-text">
                <div className="legal-cta-h">Need refund assistance?</div>
                <p className="legal-cta-p">Reach out to our support team with your transaction ID for quick resolution.</p>
                <Link to="/contact-us" className="legal-cta-btn"><Mail size={15} /> Contact support</Link>
              </div>
              <motion.div className="legal-cta-illu" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <img src={`${B}/get-start.png`} alt="Get started" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mid feature section */}
        <section className="legal-feat">
          <div className="legal-container">
            <div className="legal-feat-grid">
              <motion.div className="legal-feat-img" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.65 }}>
                <img src={`${B}/affiliate-banner-1.png`} alt="Refund process illustration" />
              </motion.div>
              <motion.div className="legal-feat-text" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: 0.1 }}>
                <div className="legal-head-circle"><TrendingUp size={24} color="#fff" /></div>
                <span className="legal-sub">How It Works</span>
                <h2 className="legal-feat-h2">Refunds processed fast, every time</h2>
                <p className="legal-feat-p">Initiate refunds directly from your A1XPAY dashboard or via our Refund API — no paperwork, no waiting on hold.</p>
                <ul className="legal-feat-list">
                  <li>Dashboard → Transactions → Select → Initiate Refund</li>
                  <li>Partial or full refund options available</li>
                  <li>Real-time status tracking in your dashboard</li>
                  <li>Automated notifications to your customer</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
